import { AddressData, Transaction, Block, DetailedTransaction, AddressStats } from '@shared/schema';

// Define a common interface for all blockchain API adapters
export interface BlockchainAPIAdapter {
  // Get wallet/address information including transactions with optional date filtering
  getAddressData(
    address: string, 
    page: number, 
    limit: number, 
    startDate: string | null, 
    endDate: string | null
  ): Promise<AddressData>;
  
  // Get detailed information about a specific transaction
  getTransactionDetails(txid: string): Promise<DetailedTransaction>;
  
  // Search blockchain for information (address, transaction, block)
  searchBlockchain(query: string): Promise<{
    type: 'address' | 'transaction' | 'block' | 'unknown',
    data: any
  }>;
  
  // Export data to CSV
  generateCsv(data: AddressData): Promise<void>;
  
  // Export data to PDF
  generatePdf(data: AddressData): Promise<void>;
}

// Factory method to get the appropriate adapter implementation
export function getBlockchainAPIAdapter(): BlockchainAPIAdapter {
  // We can select the adapter based on environment variable or config
  const apiSource = import.meta.env.VITE_BLOCKCHAIN_API_SOURCE || 'blockdaemon';
  console.log(`Using blockchain API source: ${apiSource}`);
  
  if (apiSource === 'blockstream') {
    // Future implementation of Blockstream adapter if needed
    console.warn('Blockstream adapter not implemented, falling back to Blockdaemon');
  }
  
  // Default to Blockdaemon
  return new BlockdaemonAPIAdapter();
}

// Implementation of the BlockdaemonAPIAdapter
export class BlockdaemonAPIAdapter implements BlockchainAPIAdapter {
  // Helper method to convert satoshi to BTC
  private satoshiToBtc(amount: number | string, decimals: number = 8): number {
    const amountNum = typeof amount === 'string' ? parseInt(amount, 10) : amount;
    return amountNum / Math.pow(10, decimals);
  }
  
  // Helper method for API requests - use our backend proxy to avoid CORS and API key issues
  private async fetchFromAPI(path: string, params: Record<string, string> = {}): Promise<any> {
    // Build the query string
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${path}?${queryString}` : path;
    
    const response = await fetch(`/api/blockchain${url}`);
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }
  
  // Implementation of the getAddressData method
  async getAddressData(
    address: string, 
    page: number, 
    limit: number, 
    startDate: string | null, 
    endDate: string | null
  ): Promise<AddressData> {
    console.log(`Getting address data for ${address}`);
    console.log(`Page: ${page}, Limit: ${limit}, Date range: ${startDate || 'any'} to ${endDate || 'any'}`);
    
    // Build the request params
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };
    
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    // Use our backend API which already implements Blockdaemon
    const data = await this.fetchFromAPI(`/address/${address}`, params);
    
    return data;
  }
  
  // Implementation of the getTransactionDetails method
  async getTransactionDetails(txid: string): Promise<DetailedTransaction> {
    console.log(`Getting transaction details for ${txid}`);
    
    // Use our backend API which already implements Blockdaemon
    const data = await this.fetchFromAPI(`/transaction/${txid}`);
    
    return data;
  }
  
  // Implementation of the searchBlockchain method
  async searchBlockchain(query: string): Promise<{
    type: 'address' | 'transaction' | 'block' | 'unknown',
    data: any
  }> {
    console.log(`Searching blockchain for: ${query}`);
    
    // Use our backend API which already implements Blockdaemon
    const data = await this.fetchFromAPI(`/search`, { query });
    
    return data;
  }
  
  // Implementation of export methods for client-side
  async generateCsv(data: AddressData): Promise<void> {
    console.log('Generating CSV for address data');
    
    // Create CSV content
    const header = 'Transaction ID,Date,Type,Amount,Fee,Running Balance\n';
    
    const rows = data.transactions.map(tx => {
      const date = tx.timestamp ? new Date(tx.timestamp * 1000).toISOString() : 'Pending';
      const type = tx.type.charAt(0).toUpperCase() + tx.type.slice(1);
      return `${tx.txid},${date},${type},${tx.amount},${tx.fee},${tx.balance}`;
    }).join('\n');
    
    const csvContent = `data:text/csv;charset=utf-8,${header}${rows}`;
    
    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${data.address}_transactions.csv`);
    document.body.appendChild(link);
    
    // Click the link and remove it
    link.click();
    document.body.removeChild(link);
  }
  
  async generatePdf(data: AddressData): Promise<void> {
    console.log('Generating PDF for address data');
    
    try {
      // Dynamically import jsPDF - only load when needed
      const { jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      
      // Create PDF document
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(16);
      doc.text(`Bitcoin Address: ${data.address}`, 14, 15);
      
      // Add statistics
      doc.setFontSize(12);
      doc.text('Address Summary:', 14, 25);
      doc.text(`Current Balance: ${data.stats.currentBalance} BTC`, 14, 32);
      doc.text(`Total Received: ${data.stats.totalReceived} BTC`, 14, 39);
      doc.text(`Total Sent: ${data.stats.totalSent} BTC`, 14, 46);
      
      // Add period stats if available
      if (data.stats.periodStats) {
        doc.text('Period Statistics:', 14, 56);
        doc.text(`Date Range: ${data.startDate || 'Any'} to ${data.endDate || 'Any'}`, 14, 63);
        doc.text(`Received in Period: ${data.stats.periodStats.received} BTC`, 14, 70);
        doc.text(`Sent in Period: ${data.stats.periodStats.sent} BTC`, 14, 77);
      }
      
      // Format transaction data for the table
      const tableData = data.transactions.map(tx => [
        tx.txid.substring(0, 16) + '...',
        tx.timestamp ? new Date(tx.timestamp * 1000).toLocaleDateString() : 'Pending',
        tx.type.charAt(0).toUpperCase() + tx.type.slice(1),
        `${tx.amount} BTC`,
        `${tx.fee} BTC`,
        `${tx.balance} BTC`
      ]);
      
      // Add transactions table
      autoTable(doc, {
        head: [['Transaction ID', 'Date', 'Type', 'Amount', 'Fee', 'Balance']],
        body: tableData,
        startY: data.stats.periodStats ? 85 : 56,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [66, 66, 66] }
      });
      
      // Add pagination info
      if (data.pagination) {
        const totalPages = data.pagination.totalPages;
        const currentPage = data.pagination.currentPage;
        const totalTxs = data.pagination.totalItems;
        
        doc.setFontSize(10);
        doc.text(
          `Page ${currentPage} of ${totalPages} (${data.transactions.length} of ${totalTxs} transactions)`,
          14,
          doc.internal.pageSize.height - 10
        );
      }
      
      // Generate timestamp for filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      
      // Save the PDF
      doc.save(`${data.address}_${timestamp}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw new Error('Failed to generate PDF. Please try again later.');
    }
  }
}